// Client library for Store Automation & Catalog Orders (WP-12).
// Provides types and wrappers for store connections, store campaigns, and catalog orders.

import { call } from './callable';

export type StoreType = 'woocommerce' | 'shopify' | 'salla';
export const STORE_TYPES: readonly StoreType[] = ['woocommerce', 'shopify', 'salla'] as const;

export type StoreOperation =
  | 'abandoned_cart'
  | 'order_notification'
  | 'cod_verification'
  | 'order_update';

export const STORE_OPERATIONS: readonly StoreOperation[] = [
  'abandoned_cart',
  'order_notification',
  'cod_verification',
  'order_update',
] as const;

export type CatalogOrderStatus =
  | 'pending'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'shipped'
  | 'delivered'
  | 'completed'
  | 'refunded'
  | 'cancelled';

export const CATALOG_ORDER_STATUSES: readonly CatalogOrderStatus[] = [
  'pending',
  'submitted',
  'approved',
  'rejected',
  'shipped',
  'delivered',
  'completed',
  'refunded',
  'cancelled',
] as const;

export interface StoreConnectionDoc {
  id: string;
  tenantId?: string;
  name: string;
  storeType: StoreType;
  storeUrl: string;
  status: 'active' | 'inactive' | 'error';
  config: Record<string, string>;
  stats: {
    totalOrders: number;
    abandonedCarts: number;
    codConfirmed: number;
    lastEventAt: number | null;
  };
  createdAt?: number;
  updatedAt?: number;
}

export interface StoreCampaignDoc {
  id: string;
  tenantId?: string;
  connectionId: string;
  name: string;
  storeType: StoreType;
  operation: StoreOperation;
  enabled: boolean;
  templateId: string;
  templateLanguage: string;
  delayMin: number;
  couponCode?: string | null;
  couponDiscount?: string | null;
  integrationId?: string | null;
  stats: {
    sentCount: number;
    recoveredCount: number;
    confirmedCount: number;
    lastSentAt: number | null;
  };
  createdAt?: number;
  updatedAt?: number;
}

export interface OrderItem {
  id?: string;
  name: string;
  quantity: number;
  price: number;
  currency: string;
  imageUrl?: string;
}

export interface OrderNote {
  id: string;
  note: string;
  author: string;
  createdAt: number;
}

export interface CatalogOrderDoc {
  id: string;
  tenantId?: string;
  connectionId?: string | null;
  source: 'woocommerce' | 'shopify' | 'salla' | 'whatsapp_catalog' | 'manual';
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  subscriberId?: string | null;
  items: OrderItem[];
  totalAmount: number;
  currency: string;
  shippingAddress: string;
  deliveryDate?: string | null;
  paymentMethod: string;
  paymentStatus: 'unpaid' | 'paid' | 'refunded';
  manualPaymentAttachmentUrl?: string | null;
  codStatus?: 'pending' | 'confirmed' | 'cancelled' | null;
  status: CatalogOrderStatus;
  notes: OrderNote[];
  reminderSent?: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface ManualOrderInput {
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  items: {
    name: string;
    quantity: number;
    price: number;
    currency?: string;
    imageUrl?: string;
  }[];
  totalAmount: number;
  currency?: string;
  shippingAddress: string;
  paymentMethod?: string;
  manualPaymentAttachmentUrl?: string | null;
}

// ── Callables ────────────────────────────────────────────────────────────────

const _storeConnectionSave = call<
  { id?: string; name: string; storeType: StoreType; storeUrl: string; status?: 'active' | 'inactive'; config: Record<string, any> },
  { ok: boolean; id: string }
>('storeConnectionSave');

const _storeConnectionDelete = call<{ id: string }, { ok: boolean }>('storeConnectionDelete');
const _storeConnectionList = call<{ storeType?: StoreType }, { ok: boolean; connections: StoreConnectionDoc[] }>('storeConnectionList');
const _storeConnectionGet = call<{ id: string }, { ok: boolean; connection: StoreConnectionDoc }>('storeConnectionGet');

const _storeCampaignSave = call<
  Partial<StoreCampaignDoc> & { connectionId: string; name: string; storeType: StoreType; operation: StoreOperation; templateId: string },
  { ok: boolean; id: string }
>('storeCampaignSave');

const _storeCampaignDelete = call<{ id: string }, { ok: boolean }>('storeCampaignDelete');
const _storeCampaignList = call<{ connectionId?: string; operation?: StoreOperation }, { ok: boolean; campaigns: StoreCampaignDoc[] }>('storeCampaignList');
const _storeCampaignGet = call<{ id: string }, { ok: boolean; campaign: StoreCampaignDoc }>('storeCampaignGet');

const _catalogOrderList = call<
  { status?: CatalogOrderStatus; source?: string; paymentMethod?: string; limit?: number },
  { ok: boolean; orders: CatalogOrderDoc[] }
>('catalogOrderList');

const _catalogOrderGet = call<{ id: string }, { ok: boolean; order: CatalogOrderDoc }>('catalogOrderGet');

const _catalogOrderSetStatus = call<
  { id: string; status: CatalogOrderStatus; note?: string; notifyCustomer?: boolean },
  { ok: boolean }
>('catalogOrderSetStatus');

const _catalogOrderNote = call<{ id: string; note: string }, { ok: boolean }>('catalogOrderNote');
const _catalogOrderCreateManual = call<ManualOrderInput, { ok: boolean; id: string }>('catalogOrderCreateManual');

// ── Exported API Functions ───────────────────────────────────────────────────

export async function listStoreConnections(storeType?: StoreType): Promise<StoreConnectionDoc[]> {
  const res = await _storeConnectionList({ storeType });
  return res.data.connections || [];
}

export async function getStoreConnection(id: string): Promise<StoreConnectionDoc> {
  const res = await _storeConnectionGet({ id });
  return res.data.connection;
}

export async function saveStoreConnection(input: {
  id?: string;
  name: string;
  storeType: StoreType;
  storeUrl: string;
  status?: 'active' | 'inactive';
  config: Record<string, any>;
}): Promise<{ ok: boolean; id: string }> {
  const res = await _storeConnectionSave(input);
  return res.data;
}

export async function deleteStoreConnection(id: string): Promise<{ ok: boolean }> {
  const res = await _storeConnectionDelete({ id });
  return res.data;
}

export async function listStoreCampaigns(params?: {
  connectionId?: string;
  operation?: StoreOperation;
}): Promise<StoreCampaignDoc[]> {
  const res = await _storeCampaignList(params || {});
  return res.data.campaigns || [];
}

export async function getStoreCampaign(id: string): Promise<StoreCampaignDoc> {
  const res = await _storeCampaignGet({ id });
  return res.data.campaign;
}

export async function saveStoreCampaign(input: Partial<StoreCampaignDoc> & {
  connectionId: string;
  name: string;
  storeType: StoreType;
  operation: StoreOperation;
  templateId: string;
}): Promise<{ ok: boolean; id: string }> {
  const res = await _storeCampaignSave(input);
  return res.data;
}

export async function deleteStoreCampaign(id: string): Promise<{ ok: boolean }> {
  const res = await _storeCampaignDelete({ id });
  return res.data;
}

export async function listCatalogOrders(filter?: {
  status?: CatalogOrderStatus;
  source?: string;
  paymentMethod?: string;
  limit?: number;
}): Promise<CatalogOrderDoc[]> {
  const res = await _catalogOrderList(filter || {});
  return res.data.orders || [];
}

export async function getCatalogOrder(id: string): Promise<CatalogOrderDoc> {
  const res = await _catalogOrderGet({ id });
  return res.data.order;
}

export async function setCatalogOrderStatus(input: {
  id: string;
  status: CatalogOrderStatus;
  note?: string;
  notifyCustomer?: boolean;
}): Promise<{ ok: boolean }> {
  const res = await _catalogOrderSetStatus(input);
  return res.data;
}

export async function addCatalogOrderNote(id: string, note: string): Promise<{ ok: boolean }> {
  const res = await _catalogOrderNote({ id, note });
  return res.data;
}

export async function createManualCatalogOrder(input: ManualOrderInput): Promise<{ ok: boolean; id: string }> {
  const res = await _catalogOrderCreateManual(input);
  return res.data;
}

// ── Invoice Template (WP-21) ────────────────────────────────────────────────

export interface InvoiceTemplate {
  businessNameAr: string;
  businessNameEn: string;
  tel: string;
  cr: string;
  vatNumber: string;
  branch: string;
  logoDataUrl: string | null;
  vatRate: number;
  titleAr: string;
  titleEn: string;
  referencePrefix: string;
  footerAr: string;
  thanksAr: string;
  showQr: boolean;
  paper: 'receipt80' | 'a4';
  updatedAt?: number;
}

export const DEFAULT_INVOICE_TEMPLATE: InvoiceTemplate = {
  businessNameAr: '',
  businessNameEn: '',
  tel: '',
  cr: '',
  vatNumber: '',
  branch: '',
  logoDataUrl: null,
  vatRate: 5,
  titleAr: 'فاتورة ضريبية مبسطة',
  titleEn: 'Simplified Tax Invoice',
  referencePrefix: '',
  footerAr: '',
  thanksAr: 'شكراً لتسوقكم معنا',
  showQr: true,
  paper: 'receipt80',
};

const _storeConnectionInvoiceGet = call<{ connectionId: string }, { invoice: InvoiceTemplate }>('storeConnectionInvoiceGet');
const _storeConnectionInvoiceSave = call<{ connectionId: string; invoice: InvoiceTemplate }, { ok: boolean; invoice: InvoiceTemplate }>('storeConnectionInvoiceSave');
const _storeConnectionInvoicePreview = call<{ invoice: InvoiceTemplate }, { pdfBase64: string; bytes: number }>('storeConnectionInvoicePreview');

export async function getStoreConnectionInvoice(connectionId: string): Promise<InvoiceTemplate> {
  const res = await _storeConnectionInvoiceGet({ connectionId });
  return res.data.invoice;
}

export async function saveStoreConnectionInvoice(
  connectionId: string,
  invoice: InvoiceTemplate,
): Promise<{ ok: boolean; invoice: InvoiceTemplate }> {
  const res = await _storeConnectionInvoiceSave({ connectionId, invoice });
  return res.data;
}

export async function previewStoreConnectionInvoice(
  invoice: InvoiceTemplate,
): Promise<{ pdfBase64: string; bytes: number }> {
  const res = await _storeConnectionInvoicePreview({ invoice });
  return res.data;
}

/**
 * Downscale and compress an uploaded logo file to max 600px width as PNG data URL.
 * Validates file type and guarantees base64 size stays under the 200KB limit.
 */
export function resizeLogoToDataUrl(file: File, maxWidth = 600): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.match(/^image\/(png|jpeg|webp)$/)) {
      return reject(new Error('Only PNG, JPEG, and WebP images are supported'));
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas context not available'));
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/png');
        if (dataUrl.length > 200_000) {
          return reject(new Error('Logo exceeds 200KB limit. Please use a smaller or simpler image.'));
        }
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('Failed to load image for resizing'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

